import React, { useEffect, useState } from "react";
import { getWeatherSnapshot } from "@/lib/weatherClient";

// Lightweight, CSS-only ambient weather particles that sit BEHIND Mochi.
//   - snowy → drifting snowflakes
//   - rainy → soft drizzle streaks
//   - sunny → warm radial sun-ray glow (no particles)
//   - cloudy → nothing (keeps the canvas clean)
//
// Reads the cached weather snapshot once on mount and on `weatherchange`
// events (we dispatch one when the morning check-in successfully fetches).
// The renderer is pure CSS keyframes so the compositor handles motion and
// React never re-renders during animation.

const SNOWFLAKES = [
    { left: 8,  delay: 0,   dur: 7.2, size: 6, drift: 18 },
    { left: 22, delay: 1.4, dur: 8.1, size: 4, drift: -14 },
    { left: 37, delay: 0.6, dur: 6.4, size: 7, drift: 22 },
    { left: 52, delay: 2.3, dur: 9.0, size: 5, drift: -20 },
    { left: 68, delay: 1.0, dur: 7.8, size: 6, drift: 14 },
    { left: 83, delay: 3.0, dur: 6.9, size: 4, drift: -10 },
    { left: 92, delay: 0.4, dur: 8.6, size: 5, drift: 16 },
];

const RAINDROPS = [
    { left: 10, delay: 0.0, dur: 1.3 },
    { left: 25, delay: 0.4, dur: 1.5 },
    { left: 40, delay: 0.1, dur: 1.2 },
    { left: 55, delay: 0.7, dur: 1.6 },
    { left: 70, delay: 0.3, dur: 1.4 },
    { left: 85, delay: 0.9, dur: 1.5 },
    { left: 95, delay: 0.2, dur: 1.3 },
];

export default function WeatherFx({ size = 240 }) {
    const [snap, setSnap] = useState(() => getWeatherSnapshot());

    useEffect(() => {
        const refresh = () => setSnap(getWeatherSnapshot());
        // Re-read on focus + a custom 'mochi-weather-changed' event the
        // morning check-in dispatches after a successful sky-peek.
        window.addEventListener("focus", refresh);
        window.addEventListener("mochi-weather-changed", refresh);
        return () => {
            window.removeEventListener("focus", refresh);
            window.removeEventListener("mochi-weather-changed", refresh);
        };
    }, []);

    if (!snap || !snap.condition) return null;
    const c = snap.condition;
    if (c === "cloudy") return null;

    const W = size * 1.4; // slightly wider canvas so particles drift in/out
    const H = size * 1.3;

    return (
        <div
            aria-hidden="true"
            data-testid={`weather-fx-${c}`}
            className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{ width: W, height: H, zIndex: 0 }}
        >
            {c === "sunny" && (
                <div
                    className="absolute inset-0 rounded-full"
                    style={{
                        background: "radial-gradient(circle at 50% 45%, rgba(255,220,140,0.55) 0%, rgba(255,210,120,0.25) 35%, rgba(255,200,100,0) 65%)",
                        animation: "mochi-sunpulse 6s ease-in-out infinite",
                    }}
                />
            )}

            {c === "snowy" && SNOWFLAKES.map((f, i) => (
                <span
                    // SNOWFLAKES is a module-level const — `i` IS the stable
                    // identity. Prefixed for clarity / linter consistency.
                    key={`snow-${i}`}
                    className="absolute rounded-full bg-white/80 dark:bg-white/70"
                    style={{
                        left: `${f.left}%`,
                        top: "-10%",
                        width: f.size,
                        height: f.size,
                        boxShadow: "0 0 6px rgba(255,255,255,0.7)",
                        animation: `mochi-snow-${i} ${f.dur}s linear ${f.delay}s infinite`,
                        // Per-instance keyframes injected via the <style> block below
                    }}
                />
            ))}

            {c === "rainy" && RAINDROPS.map((d, i) => (
                <span
                    // RAINDROPS is a module-level const — `i` IS the stable
                    // identity. Prefixed for clarity / linter consistency.
                    key={`rain-${i}`}
                    className="absolute rounded-full bg-sky-300/50 dark:bg-sky-200/40"
                    style={{
                        left: `${d.left}%`,
                        top: "-8%",
                        width: 2,
                        height: 10,
                        animation: `mochi-rain ${d.dur}s linear ${d.delay}s infinite`,
                    }}
                />
            ))}

            {/* Per-snowflake keyframes (sway varies per flake) */}
            {c === "snowy" && (
                <style>
                    {SNOWFLAKES.map((f, i) => `
                        @keyframes mochi-snow-${i} {
                            0%   { transform: translate3d(0, 0, 0); opacity: 0; }
                            10%  { opacity: 0.9; }
                            50%  { transform: translate3d(${f.drift}px, ${H * 0.55}px, 0); }
                            90%  { opacity: 0.85; }
                            100% { transform: translate3d(${-f.drift}px, ${H + 20}px, 0); opacity: 0; }
                        }
                    `).join("\n")}
                </style>
            )}
        </div>
    );
}
